import * as React from 'react';

import {ActionMenu} from '../../action-menu/action-menu';
import {colors} from '../../colors/colors';
import {StackLegacy} from '../../layout/stack/stack-legacy';
import type {SelectMenuOption} from '../../select-menu/select-menu';
import {StatusBadge} from '../../status-badge/status-badge';
import type {StatusBadgeProps} from '../../status-badge/status-badge';
import {Truncated} from '../../truncated/truncated';

import styles from './thumbnail-content.module.css';

export type Props = {
    title: string | React.ReactNode;
    shouldTruncateTitle?: boolean;
    secondaryInfo?: string | React.ReactNode;
    tertiaryInfo?: string | React.ReactNode;
    thumbnail?: string | React.ReactNode;
    thumbnailHeight?: number;
    cardWidth?: number;
    options?: SelectMenuOption[];
    getActionMenuRef?: (arg1?: HTMLElement | null | undefined) => void;
    statusBadge?: StatusBadgeProps | null | undefined;
    additionalStatusBadges?: StatusBadgeProps[];
    rightAlignedStatusBadge?: StatusBadgeProps | null | undefined;
    backgroundColor?: string;
};

export function ThumbnailContent(props: Props) {
    const {backgroundColor = colors.white} = props;

    const hasStatusBadges =
        props.statusBadge ||
        props.rightAlignedStatusBadge ||
        (props.additionalStatusBadges && props.additionalStatusBadges.length > 0);

    return (
        <div style={{width: props.cardWidth}} className='full-height'>
            {props.thumbnail && (
                <div
                    style={{height: props.thumbnailHeight, backgroundColor}}
                    className={styles['thumbnail-container']}
                >
                    {typeof props.thumbnail === 'string' ? (
                        // @ts-expect-error
                        <img className='full-width' src={props.thumbnail} alt={props.title} />
                    ) : (
                        props.thumbnail
                    )}
                </div>
            )}
            <div className={styles['content-container']}>
                {hasStatusBadges && (
                    <div className='flex full-width justify-sb'>
                        <StackLegacy isHorizontal={true} spacing={6}>
                            {props.statusBadge && (
                                <StatusBadge
                                    level={props.statusBadge.level}
                                    text={props.statusBadge.text}
                                    detail={props.statusBadge.detail}
                                    size='small'
                                />
                            )}
                            {props.additionalStatusBadges &&
                                props.additionalStatusBadges.map((statusBadge) => (
                                    <StatusBadge
                                        key={`${statusBadge.level}-${statusBadge.text}`}
                                        level={statusBadge.level}
                                        text={statusBadge.text}
                                        detail={statusBadge.detail}
                                        size='small'
                                    />
                                ))}
                        </StackLegacy>
                        {props.rightAlignedStatusBadge && (
                            <div className='pr-16'>
                                <StatusBadge
                                    level={props.rightAlignedStatusBadge.level}
                                    text={props.rightAlignedStatusBadge.text}
                                    detail={props.rightAlignedStatusBadge.detail}
                                    size='small'
                                />
                            </div>
                        )}
                    </div>
                )}
                <div className={styles['title-secondary-info-container']}>
                    {typeof props.title === 'string' ? (
                        <strong className={styles.title}>
                            {props.shouldTruncateTitle ? (
                                <Truncated title={props.title}>{props.title}</Truncated>
                            ) : (
                                props.title
                            )}
                        </strong>
                    ) : (
                        props.title
                    )}
                    {props.secondaryInfo && (
                        <div className={styles['secondary-info']}>{props.secondaryInfo}</div>
                    )}
                </div>
                {props.tertiaryInfo && (
                    <div className={styles['tertiary-info']}>{props.tertiaryInfo}</div>
                )}
                {props.options && props.options.length && (
                    <div className={styles['action-menu']}>
                        <ActionMenu
                            as='div'
                            options={props.options}
                            getRef={props.getActionMenuRef}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
